import { audienceOfRoles, isStaffPermission, permissionsOfRoles, type Audience, type Permission } from '@infinix/contract';
import type { AppEntry, NavItem } from './index';

/**
 * Who may see and do what in the shell (A-22, A-34), as plain functions: the shell's session and menus use them, and
 * test/access.test.ts checks them for every kind of person. The backends enforce the same rules on their own.
 */

/** What the shell knows about the signed-in person and the scope they are looking at. */
export interface Viewer {
  roles: readonly string[];
  /** The organization in view: a customer's own; for staff, the tenant they picked (null at All tenants). */
  orgPicked: boolean;
  /** The business unit in view (null = All business units). */
  unitPicked: boolean;
}

export interface Access {
  audience: Audience;
  /** Staff looking at a tenant: they see its apps as its admins do, read only. */
  staffView: boolean;
  /** May see it: pages, menus, data. */
  can: (perm: Permission | undefined) => boolean;
  /** May do it: only the person's own permissions, never the viewed admin's. */
  canAct: (perm: Permission | undefined) => boolean;
}

/**
 * The person's access. Staff with a tenant picked also see what that tenant's organization admin sees (with a unit
 * picked: the unit admin), never act with it. A customer never acts with a staff permission.
 */
export function accessOf(v: Viewer): Access {
  const own = permissionsOfRoles(v.roles);
  const audience = audienceOfRoles(v.roles);
  const staffView = audience === 'staff' && v.orgPicked;
  const viewed = staffView ? permissionsOfRoles([v.unitPicked ? 'bu_admin' : 'org_admin']) : new Set<Permission>();
  const can = (perm: Permission | undefined) => !perm || own.has(perm) || viewed.has(perm);
  const canAct = (perm: Permission | undefined) => !perm || (own.has(perm) && !(isStaffPermission(perm) && audience !== 'staff'));
  return { audience, staffView, can, canAct };
}

/**
 * Whether a menu item shows. Staff in a customer app: with a tenant picked, the menu its admins see plus staff-only
 * items; at All tenants, only what works across tenants (items that declare a platform permission). Everyone else: the
 * item's own permission.
 */
export function menuItemVisible(a: Access, app: Pick<AppEntry, 'audience'>, item: Pick<NavItem, 'perm' | 'anyPerm' | 'platformPerm'>): boolean {
  if (a.audience === 'staff' && app.audience !== 'staff') {
    if (a.staffView) return (Boolean(item.perm) && a.can(item.perm)) || Boolean(item.anyPerm?.some((p) => a.can(p))) || Boolean(item.platformPerm && a.can(item.platformPerm));
    return Boolean(item.platformPerm && a.can(item.platformPerm));
  }
  return a.can(item.perm) || Boolean(item.anyPerm?.some((p) => a.can(p)));
}

export const NO_ROLE_REASON = "Your role doesn't include this action";
/** Why staff looking at a tenant can't act (A-34): the tenant's own people make changes. */
export const STAFF_VIEW_REASON = "Staff view: the tenant's admins make this change";

/** Whether an action button is enabled, and why not. `extra` is a screen's own rule, explained by `deniedReason`. */
export function actionState(a: Access, perm: Permission, extra = true, deniedReason?: string): { ok: boolean; reason: string | undefined } {
  const permitted = a.canAct(perm);
  if (permitted && extra) return { ok: true, reason: undefined };
  if (!permitted) return { ok: false, reason: a.staffView && a.can(perm) ? STAFF_VIEW_REASON : NO_ROLE_REASON };
  return { ok: false, reason: deniedReason ?? NO_ROLE_REASON };
}
